<template>
  <v-card outlined class="info-card team-vacations-card">
    <v-card-title class="info-title d-flex align-center">
      <v-icon size="18" class="mr-2">mdi-account-multiple-check</v-icon>
      {{ t("employeeVacationsTeam.title") }}
      <v-spacer></v-spacer>
      <div class="view-switch mr-2">
        <v-btn
          size="x-small"
          variant="text"
          :color="viewMode === 'week' ? 'primary' : 'default'"
          @click="setViewMode('week')"
        >
          {{ t("employeeVacationsTeam.view.week") }}
        </v-btn>
        <v-btn
          size="x-small"
          variant="text"
          :color="viewMode === 'month' ? 'primary' : 'default'"
          @click="setViewMode('month')"
        >
          {{ t("employeeVacationsTeam.view.month") }}
        </v-btn>
      </div>
      <v-btn
        icon
        variant="text"
        size="small"
        class="mr-1"
        :title="t('employeeVacationsTeam.view.prev')"
        @click="goPrevious"
      >
        <v-icon size="18">mdi-chevron-left</v-icon>
      </v-btn>
      <v-btn
        icon
        variant="text"
        size="small"
        class="mr-1"
        :title="t('employeeVacationsTeam.view.next')"
        @click="goNext"
      >
        <v-icon size="18">mdi-chevron-right</v-icon>
      </v-btn>
      <v-btn size="small" variant="text" color="primary" class="mr-2" @click="resetToToday">
        {{ t("employeeVacationsTeam.view.today") }}
      </v-btn>
      <span class="view-title text-medium-emphasis">{{ viewTitle }}</span>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text>
      <v-alert
        v-if="!canDisplay"
        type="info"
        density="comfortable"
        class="mb-3"
      >
        {{ t("employeeVacationsTeam.alerts.role") }}
      </v-alert>

      <v-alert
        v-else-if="!managerId"
        type="warning"
        density="comfortable"
        class="mb-3"
      >
        {{ t("employeeVacationsTeam.alerts.missingManager") }}
      </v-alert>

      <v-alert
        v-else-if="errorMessage"
        type="error"
        density="comfortable"
        class="mb-3"
      >
        {{ errorMessage }}
      </v-alert>

      <div v-else>
        <div v-if="loading" class="d-flex justify-center py-6">
          <v-progress-circular indeterminate color="primary" size="32" />
        </div>

        <div v-else>
          <div v-if="!teamMembers.length" class="text-caption text-medium-emphasis">
            {{ t("employeeVacationsTeam.alerts.noTeam") }}
          </div>

          <div v-else class="timeline-container">
            <div class="timeline-header">
              <div class="timeline-name-cell header-cell">{{ t("employeeVacationsTeam.table.employee") }}</div>
              <div class="timeline-grid header-grid" :style="gridTemplateStyle">
                <div
                  v-for="day in timelineDays"
                  :key="day.iso"
                  class="timeline-cell header-cell"
                  :class="{
                    'is-weekend': day.isWeekend,
                    'is-today': day.isToday,
                  }"
                >
                  <div class="day-number">{{ day.day }}</div>
                  <div class="day-label">{{ day.weekday }}</div>
                </div>
              </div>
            </div>

            <div
              v-for="row in timelineRows"
              :key="row.employee.id"
              class="timeline-row"
            >
              <div class="timeline-name-cell">
                <div class="employee-name">{{ row.employee.fullName }}</div>
                <div class="employee-meta text-caption text-medium-emphasis">
                  {{ row.employee.position || '—' }}
                </div>
              </div>
              <div class="timeline-grid" :style="gridTemplateStyle">
                <div
                  v-for="day in timelineDays"
                  :key="`${row.employee.id}-${day.iso}`"
                  class="timeline-cell"
                  :class="{
                    'is-weekend': day.isWeekend,
                    'is-today': day.isToday,
                  }"
                ></div>
                <div
                  v-for="event in row.events"
                  :key="event.id"
                  class="timeline-event"
                  :class="`status-${event.status}`"
                  :style="{
                    gridColumn: `${event.startColumn} / ${event.endColumn}`,
                  }"
                  :title="event.tooltip"
                >
                  <span class="event-title sr-only">{{ event.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="timeline-legend mt-4">
            <div class="legend-item">
              <span class="legend-dot status-approved"></span>
              {{ t("employeeVacationsTeam.status.approved") }}
            </div>
            <div class="legend-item">
              <span class="legend-dot status-pending"></span>
              {{ t("employeeVacationsTeam.status.pending") }}
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios";
import { defineComponent, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  formatAsIsoDate,
  normalizeDateString,
  toDateObject,
} from "@/Utile/holidayFormatting.mjs";
import { formatDisplayDayMonth, formatDisplayWeekday, formatDisplayMonthYear } from "@/Utile/displayFormat.mjs";

const STATUS_META_KEYS = {
  approved: { color: "success", labelKey: "employeeVacationsTeam.status.approved" },
  pending: { color: "warning", labelKey: "employeeVacationsTeam.status.pending" },
};

const DISPLAYED_STATUSES = new Set(["approved", "pending"]);
const MAX_TEAM_MEMBERS = 10;

const differenceInDays = (start, end) => {
  const MS = 1000 * 60 * 60 * 24;
  const startUTC = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((endUTC - startUTC) / MS);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export default defineComponent({
  name: "EmployeeVacationsTeam",
  props: {
    managerId: {
      type: [Number, String],
      default: null,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    role: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const { t, locale } = useI18n();
    const loading = ref(false);
    const errorMessage = ref("");
    const teamMembers = ref([]);
    const requestsByEmployee = ref({});
    const viewMode = ref("month");
    const anchorDate = ref(new Date());

    const canDisplay = computed(() => {
      const rawRoles = localStorage.getItem("user-roles");
      let roles = [];
      try {
        roles = JSON.parse(rawRoles || "[]");
      } catch (e) {
        roles = [];
      }
      const set = new Set();
      roles.forEach((entry) => {
        if (!entry) return;
        const key = typeof entry === "string" ? entry : entry.key || entry.role;
        const norm = key ? key.toLowerCase() : null;
        if (norm) {
          set.add(norm);
        }
      });
      const legacy = (props.role || localStorage.getItem("user-role") || "").toString().toLowerCase();
      if (legacy) set.add(legacy);
      return set.has("vacation-manager") || set.has("vacation-director");
    });

    const statusMeta = computed(() => ({
      approved: { color: "success", label: t("employeeVacationsTeam.status.approved") },
      pending: { color: "warning", label: t("employeeVacationsTeam.status.pending") },
    }));

    const viewStart = computed(() => {
      const base = new Date(anchorDate.value);
      base.setHours(0, 0, 0, 0);
      if (viewMode.value === "month") {
        base.setDate(1);
        return base;
      }
      const day = base.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      base.setDate(base.getDate() + diff);
      return base;
    });

    const viewEnd = computed(() => {
      const start = new Date(viewStart.value);
      if (viewMode.value === "month") {
        const end = new Date(start);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
        return end;
      }
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return end;
    });

    const timelineDays = computed(() => {
      const days = [];
      const cursor = new Date(viewStart.value);
      const end = viewEnd.value;
      while (cursor <= end) {
        const iso = formatAsIsoDate(cursor);
        const weekdayIndex = cursor.getDay();
        days.push({
          iso,
          day: cursor.getDate(),
          weekday: formatDisplayWeekday(cursor, locale?.value, { style: "short" }),
          isWeekend: weekdayIndex === 0 || weekdayIndex === 6,
          isToday: iso === formatAsIsoDate(new Date()),
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      return days;
    });

    const gridTemplateStyle = computed(() => ({
      gridTemplateColumns: `repeat(${timelineDays.value.length}, minmax(36px, 1fr))`,
    }));

    const viewTitle = computed(() => {
      const start = viewStart.value;
      const end = viewEnd.value;
      if (viewMode.value === "month") {
        return formatDisplayMonthYear(start, locale?.value);
      }
      return `${formatDisplayDayMonth(start)} – ${formatDisplayDayMonth(end)} ${end.getFullYear()}`;
    });

    const normalizedTeam = computed(() =>
      teamMembers.value
        .slice(0, MAX_TEAM_MEMBERS)
        .map((emp) => ({
          id: emp.id,
          fullName: [emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.email || t("employeeVacationsTeam.table.unknownEmployee"),
          position: emp.position || emp.department || "",
          companyId: emp.company_id,
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName, locale?.value || "ro")),
    );

    const requestsMap = computed(() => requestsByEmployee.value || {});

    const timelineRows = computed(() => {
      const start = viewStart.value;
      const end = viewEnd.value;
      const totalDays = timelineDays.value.length;

      return normalizedTeam.value.map((employee) => {
        const requests = requestsMap.value[employee.id] || [];
        const events = [];

        requests
          .filter((request) => DISPLAYED_STATUSES.has(String(request.status || "").toLowerCase()))
          .forEach((request) => {
            const startIso = normalizeDateString(request.start_date);
            const endIso = normalizeDateString(request.end_date || request.start_date);
            const startDate = toDateObject(startIso);
            const endDate = toDateObject(endIso || startIso);
            if (!startDate || !endDate) return;

            if (endDate < start || startDate > end) return;

            const startIndex = clamp(differenceInDays(start, startDate), 0, totalDays - 1);
            const endIndex = clamp(differenceInDays(start, endDate), 0, totalDays - 1);

            const status = String(request.status || "").toLowerCase();
            const statusMetaEntry = statusMeta.value[status] || statusMeta.value.pending;
            const label =
              request.holiday_type_name ||
              statusMetaEntry.label ||
              t("employeeVacations.calendar.unknownType");

            const tooltip = [
              label,
              `${startIso || "—"} → ${endIso || "—"}`,
              statusMetaEntry.label ? t("employeeVacationsTeam.tooltip.status", { status: statusMetaEntry.label }) : null,
              request.notes ? t("employeeVacationsTeam.tooltip.notes", { notes: request.notes }) : null,
            ]
              .filter(Boolean)
              .join("\n");

            events.push({
              id: `${request.id}-${startIndex}-${endIndex}`,
              startColumn: startIndex + 1,
              endColumn: endIndex + 2,
              status,
              label,
              tooltip,
            });
          });

        return {
          employee,
          events,
        };
      });
    });

    const fetchRequestsForEmployee = async (employee) => {
      const companyContext = props.companyId || employee.company_id;
      if (!companyContext) {
        return [];
      }
      try {
        const params = {
          company_id: companyContext,
          employee_id: employee.id,
          status: "approved,pending",
        };
        const { data } = await axios.get("/holidays/requests", { params });
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error(`Eroare la obținerea concediilor pentru ${employee.id}:`, error);
        return [];
      }
    };

    const fetchTeam = async () => {
      if (!canDisplay.value || !props.managerId) {
        teamMembers.value = [];
        requestsByEmployee.value = {};
        return;
      }

      loading.value = true;
      errorMessage.value = "";

      try {
        const params = props.companyId ? { company_id: props.companyId } : {};
        const { data } = await axios.get("/candidati/employees", { params });
        const employees = Array.isArray(data) ? data : [];
        const directReports = employees.filter(
          (emp) => Number(emp.id_manager) === Number(props.managerId)
        );

        const limitedReports = directReports.slice(0, MAX_TEAM_MEMBERS);
        teamMembers.value = limitedReports;

        const requestEntries = await Promise.all(
          limitedReports.map(async (emp) => {
            const requests = await fetchRequestsForEmployee(emp);
            return [emp.id, requests];
          })
        );

        const map = {};
        requestEntries.forEach(([id, requests]) => {
          map[id] = requests;
        });
        requestsByEmployee.value = map;
      } catch (error) {
        console.error("Eroare la obținerea subordonaților:", error);
        errorMessage.value = "Nu am putut încărca lista subordonaților.";
      } finally {
        loading.value = false;
      }
    };

    const goPrevious = () => {
      const next = new Date(anchorDate.value);
      if (viewMode.value === "month") {
        next.setMonth(next.getMonth() - 1);
      } else {
        next.setDate(next.getDate() - 7);
      }
      anchorDate.value = next;
    };

    const goNext = () => {
      const next = new Date(anchorDate.value);
      if (viewMode.value === "month") {
        next.setMonth(next.getMonth() + 1);
      } else {
        next.setDate(next.getDate() + 7);
      }
      anchorDate.value = next;
    };

    const resetToToday = () => {
      anchorDate.value = new Date();
    };

    const setViewMode = (mode) => {
      if (viewMode.value === mode) return;
      viewMode.value = mode;
      anchorDate.value = new Date(anchorDate.value);
    };

    watch(
      () => [props.managerId, props.companyId, canDisplay.value],
      () => {
        fetchTeam();
      },
      { immediate: true }
    );

    return {
      loading,
      errorMessage,
      teamMembers: normalizedTeam,
      timelineDays,
      timelineRows,
      gridTemplateStyle,
      viewTitle,
      goPrevious,
      goNext,
      resetToToday,
      setViewMode,
      viewMode,
      canDisplay,
      managerId: props.managerId,
      t,
    };
  },
});
</script>

<style scoped>
.team-vacations-card {
  margin-top: 16px;
}

.view-switch {
  display: flex;
  gap: 4px;
  align-items: center;
}

.view-title {
  font-weight: 500;
  text-transform: capitalize;
}

.timeline-container {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background-color: #f8fafc;
  box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.6);
}

.timeline-header,
.timeline-row {
  display: grid;
  grid-template-columns: 220px 1fr;
}

.timeline-name-cell {
  padding: 12px;
  border-right: 1px solid #e2e8f0;
  background-color: #f8fafc;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.timeline-name-cell.header-cell {
  background-color: #edf2f7;
  font-weight: 600;
}

.employee-name {
  font-weight: 600;
}

.employee-meta {
  margin-top: 4px;
}

.timeline-grid {
  position: relative;
  display: grid;
  background-color: #fff;
  gap: 2px;
}

.header-grid {
  background-color: #edf2f7;
}

.timeline-cell {
  min-height: 20px;
  position: relative;
  background-color: transparent;
}

.timeline-cell.is-weekend {
  background-color: rgba(226, 232, 240, 0.55);
}

.timeline-cell.is-today {
  background-color: rgba(59, 130, 246, 0.18);
}

.timeline-event {
  position: relative;
  height: 20px;
  width: calc(100% - 6px);
  margin: 0 auto;
  border-radius: 8px;
  transition: box-shadow 0.2s ease;
  justify-self: stretch;
  align-self: stretch;
}

.timeline-event.status-approved {
  background-color: rgba(34, 197, 94, 0.28);
  box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.45);
}

.timeline-event.status-pending {
  background-color: rgba(251, 191, 36, 0.3);
  box-shadow: inset 0 0 0 1px rgba(251, 191, 36, 0.45);
}

.timeline-event:hover {
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.25), 0 6px 18px rgba(15, 23, 42, 0.08);
}

.timeline-row:nth-child(odd) .timeline-cell {
  background-color: rgba(248, 250, 252, 0.85);
}

.timeline-row:nth-child(odd) .timeline-cell.is-weekend {
  background-color: rgba(226, 232, 240, 0.55);
}

.timeline-row:nth-child(odd) .timeline-cell.is-today {
  background-color: rgba(59, 130, 246, 0.18);
}

.day-number {
  font-size: 13px;
  font-weight: 600;
}

.day-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #4a5568;
}

.timeline-legend {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #334155;
}

.legend-dot {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  display: inline-block;
  border: 1px solid rgba(148, 163, 184, 0.4);
}

.legend-dot.status-approved {
  background-color: rgba(34, 197, 94, 0.28);
  border-color: rgba(34, 197, 94, 0.5);
}

.legend-dot.status-pending {
  background-color: rgba(251, 191, 36, 0.28);
  border-color: rgba(251, 191, 36, 0.5);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
